// Things the crab pulls out or puts into the world. All drawn here as pixel art, no game files.
// Coordinates follow the crab's: x = 0 at the anchor, rows above it negative.

import { hash } from './core.js';
import { linePx, sprite } from './pixels.js';
import { mixHex } from './color.js';

// ---------- parachute ----------
// One color in a pattern. The canopy is drawn with '@main' tags, so the look that draws it
// colors it: the companion's own look by default (it matches its skin), or any other skin, and a
// gold or rainbow parachute shimmers like the skin does. Patterns add a tint of the same color
// (lighter, or darker on very light colors), a darker shade, or colors of their own (rainbow,
// creeper).
export const CHUTE_PATTERNS = { plain: 'Plain', stripes: 'Stripes', dots: 'Polka dots', checkers: 'Checkers', rainbow: 'Rainbow', creeper: 'Creeper' };

const GORES = ['#E8453C', '#F39C2B', '#F4D03F', '#4CBB5E', '#3B8FE0', '#8E5BD6'];
const GORE_SHADES = GORES.map(c => [mixHex(c, 1.25), c, mixHex(c, 0.8)]);   // top row, middle, edge
const CREEPER_GREENS = ['#5DB33F', '#4C9A34', '#7AC75A', '#3E8A2B'];
// The creeper's face, as 'column,row' with rows counted up from the scalloped edge (0) to the top (-5).
const CREEPER_FACE = new Set([
  ...[-6, -5, -4, 3, 4, 5].flatMap(c => [`${c},-4`, `${c},-3`]),   // eyes
  '-1,-2', '0,-2',                                                   // nose
  ...[-3, -2, -1, 0, 1, 2].map(c => `${c},-1`),                      // mouth
]);
const gore = c => Math.floor((c + 12) / 4);                          // which panel a column is in
// Each pattern turns a canopy pixel (column c, row r from the edge, its plain tag) into a tag or color.
const PAINT = {
  plain: (c, r, tag) => tag,
  stripes: (c, r, tag) => (gore(c) % 2 ? '@+main' : tag),
  dots: (c, r, tag) => ((r === -3 && (c + 12) % 6 === 2) || (r === -1 && (c + 12) % 6 === 5) ? '@+main' : tag),
  checkers: (c, r, tag) => ((Math.floor((c + 12) / 2) + Math.floor((r + 6) / 2)) % 2 ? '@~main' : tag),
  rainbow: (c, r) => GORE_SHADES[gore(c) % GORES.length][r === -5 ? 0 : r === 0 ? 2 : 1],
  creeper: (c, r) => (CREEPER_FACE.has(`${c},${r}`) ? '#1E1E1E' : CREEPER_GREENS[Math.floor(hash(c + 20, r + 20, 7) * 4)]),
};

export function chutePixels(open, top, handL, handR, pattern = 'plain') {
  const canopy = [], strings = [], paint = PAINT[pattern] || PAINT.plain;
  const lim = 11.5 * open + 0.6;                // half width while it opens
  const base = top - 9;                         // the scalloped bottom edge
  const rows = [[base - 5, -5, 4], [base - 4, -8, 7], [base - 3, -10, 9], [base - 2, -11, 10], [base - 1, -12, 11]];
  for (const [r, c0, c1] of rows) for (let c = c0; c <= c1; c++) {
    if (Math.abs(c + 0.5) <= lim) canopy.push([c, r, paint(c, r - base, r === base - 5 ? '@^main' : '@main')]);
  }
  for (let c = -12; c <= 11; c += 2) if (Math.abs(c + 0.5) <= lim) canopy.push([c, base, paint(c, 0, '@_main')]);
  const cl = Math.round(-0.5 - lim + 0.6), cr = Math.round(-0.5 + lim - 0.6);
  strings.push(...linePx(cl, base + 1, handL[0], handL[1] - 1, '#E9E2D8'), ...linePx(cr, base + 1, handR[0], handR[1] - 1, '#E9E2D8'));
  return { canopy, strings };
}

// ---------- deck chair (from the front, as in the first preview) ----------
// (0, 0) is the floor under the chair's middle. The crab sits on the seat bar at row -4.
export const CHAIR_SEAT = { row: -4, crabDx: 0 };
const WOOD = '#8A5A33';
export function chairPixels() {
  const red = '#D9573F', cream = '#F3E6D0', px = [];
  const at = (c, r, t) => px.push([c - 8, r - 11, t]);
  for (let c = 1; c <= 14; c++) at(c, 0, WOOD);
  for (let r = 1; r <= 6; r++) for (let c = 2; c <= 13; c++) at(c, r, Math.floor((c - 2) / 2) % 2 ? cream : red);
  for (const c of [1, 14]) for (let r = 0; r <= 10; r++) at(c, r, WOOD);
  at(0, 5, WOOD); at(15, 5, WOOD);
  for (let c = 0; c <= 15; c++) at(c, 7, WOOD);
  at(0, 10, WOOD); at(15, 10, WOOD);
  return px;
}

// A small drink with an umbrella, for the deck chair.
export const DRINK = [[0, 0, '#F2A23A'], [0, -1, '#BFE8F2'], [1, -1, '#BFE8F2'], [1, 0, '#F2A23A'], [1, -2, '#E0473F'], [0, -3, '#F25AA0'], [1, -3, '#F25AA0'], [-1, -3, '#F25AA0']];

// ---------- the bed (from the front) ----------
// (0, 0) is the floor under the bed's middle. The sleeper lies on the mattress at row -4 and
// the blanket is drawn in front of it, so only its head and nightcap show on the pillow.
export const BED = { surfaceRow: -4, halfWidth: 10 };
const BEDWOOD = '#8A5A33', PANEL = '#A87445', PILLOW = '#F4F1EA', SHEET = '#E6E1D8';
export function bedBackPixels() {
  const px = [];
  for (let c = -9; c <= 8; c++) px.push([c, -12, BEDWOOD]);                      // headboard, rounded top
  for (let r = -11; r <= -5; r++) for (let c = -10; c <= 9; c++) px.push([c, r, c === -10 || c === 9 || r === -11 ? BEDWOOD : PANEL]);
  for (let r = -8; r <= -7; r++) for (let c = -5; c <= 4; c++) px.push([c, r, PILLOW]);
  for (let c = -10; c <= 9; c++) px.push([c, -4, SHEET]);                         // mattress
  for (const c of [-10, -9, 8, 9]) for (const r of [-2, -1]) px.push([c, r, BEDWOOD]);   // legs
  return px;
}
// The blanket rises a row with each breath in.
export function blanketPixels(breathIn) {
  const px = [], top = breathIn ? -6 : -5, blue = '#3B5BDB', stripe = '#6B85E8';
  for (let c = -10; c <= 9; c++) px.push([c, top, PILLOW]);                       // the folded sheet
  for (let r = top + 1; r <= -3; r++) for (let c = -10; c <= 9; c++) px.push([c, r, (c + 20) % 4 < 2 ? blue : stripe]);
  return px;
}

// ---------- a file to throw (for file basketball) ----------
export const FILE = [[0, -2, '#FFFFFF'], [1, -2, '#FFFFFF'], [2, -2, '#C9D3E0'], [0, -1, '#FFFFFF'], [1, -1, '#9AA7B8'], [2, -1, '#FFFFFF'],
  [0, 0, '#FFFFFF'], [1, 0, '#9AA7B8'], [2, 0, '#FFFFFF'], [0, 1, '#FFFFFF'], [1, 1, '#FFFFFF'], [2, 1, '#FFFFFF']];

// ---------- the party: a big speaker and a light stand ----------
// The colors the party lights cycle through.
export const PARTY_COLORS = ['#FF3EA5', '#3EC9FF', '#7CFF4F', '#FFD23F', '#B45CFF', '#FF7A3E'];

// Distance of cell (c, r) from the middle of an n x n square, for drawing round speaker cones.
const fromMiddle = (n, c, r) => Math.hypot(c - (n - 1) / 2, r - (n - 1) / 2);

// (0, 0) is the surface under the speaker's middle: a cabinet 12 wide and 16 tall on two feet.
// pump (0..1) pushes the woofer out on the beat; playing turns the light green.
export const SPEAKER_ROWS = 17;
export function speakerPixels(pump, playing) {
  const px = [], CAB = '#1E1E24', EDGE = '#3A3A46', DARK = '#121216';
  const at = (c, r, t) => px.push([c - 6, r - SPEAKER_ROWS, t]);
  for (let r = 0; r < 16; r++) for (let c = 0; c < 12; c++) at(c, r, r === 0 || c === 0 ? EDGE : r === 15 || c === 11 ? DARK : CAB);
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) {                   // the tweeter
    const d = fromMiddle(4, c, r);
    if (d < 2) at(4 + c, 2 + r, d < 1 ? '#9A9AAA' : '#4A4A58');
  }
  for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) {                   // the woofer
    const d = fromMiddle(8, c, r);
    if (d > 4.1) continue;
    at(2 + c, 7 + r, d > 3.3 ? '#4E4E5C' : d < 1 + pump ? '#6E6E80' : pump > 0.5 && d > 2.2 ? '#5A5A6A' : '#2A2A33');
  }
  at(9, 5, playing ? '#5CFF7A' : '#7A2E2E');
  for (const c of [1, 10]) at(c, 16, DARK);
  return px;
}

// A light stand: a tripod and pole, a bar with a lamp under each end, a mirror ball on top.
// (0, 0) is the surface under the pole. `lit` lamps are on; beat turns the ball and the colors.
export const STAND = { rows: 22, lamps: [[-6, -15], [5, -15]], ball: { c: 0.5, r: -19.5 } };
const BALL = ['.XXX.', 'XXXXX', 'XXXXX', 'XXXXX', '.XXX.'];
export function standPixels(beat, lit) {
  const px = [], POLE = '#3A3A46', HI = '#5A5A68', CAN = '#24242C';
  px.push([-3, -1, POLE], [3, -1, POLE], [-2, -2, POLE], [2, -2, POLE], [-1, -3, POLE], [1, -3, POLE]);
  for (let r = -15; r <= -2; r++) px.push([0, r, r % 5 === 0 ? HI : POLE]);
  for (let c = -6; c <= 6; c++) px.push([c, -16, POLE]);
  STAND.lamps.forEach(([c, r], i) => {
    const color = i < lit ? PARTY_COLORS[(beat + i * 3) % PARTY_COLORS.length] : '#5A5A66';
    px.push([c, r, CAN], [c + 1, r, CAN], [c, r + 1, color], [c + 1, r + 1, color]);
  });
  px.push([0, -17, POLE]);
  const cells = [];
  BALL.forEach((row, r) => [...row].forEach((ch, c) => { if (ch === 'X') cells.push([c - 2, r - 22]); }));
  const glint = cells[(beat * 7) % cells.length];
  for (const [c, r] of cells) {
    const tile = Math.abs(c + r + beat) % 2 ? '#D7DEE8' : '#8E99A8';
    px.push([c, r, lit && c === glint[0] && r === glint[1] ? '#FFFFFF' : tile]);
  }
  return px;
}

// Music notes that float up from the speaker, in two shapes. The color is filled in per note.
export const NOTES = [sprite(`
..x.
..xx
..x.
xxx.
xx..
`, { x: 'N' }), sprite(`
.xxxx
.x..x
.x..x
xx.xx
x..x.
`, { x: 'N' })];

// ---------- the sandcastle home ----------
// (0, 0) is the floor at the castle's left edge.
export function castlePixels() {
  const sand = '#E3C489', shade = '#C9A86A', dark = '#3B2A1C', pole = '#7A5A3A', px = [];
  const at = (c, r, t) => px.push([c, r - 18, t]);
  for (const x0 of [0, 18]) {
    for (let r = 3; r <= 17; r++) for (let c = x0; c <= x0 + 5; c++) at(c, r, c === x0 + 5 ? shade : sand);
    for (const c of [x0, x0 + 2, x0 + 4]) at(c, 2, sand);
    at(x0 + 2, 7, dark); at(x0 + 2, 8, dark);
  }
  for (let r = 7; r <= 17; r++) for (let c = 6; c <= 17; c++) at(c, r, sand);
  for (const c of [6, 8, 10, 12, 14, 16]) at(c, 6, sand);
  for (let c = 0; c <= 23; c++) at(c, 17, shade);
  for (let r = 11; r <= 17; r++) for (let c = 9; c <= 14; c++) at(c, r, dark);
  for (let c = 10; c <= 13; c++) at(c, 10, dark);
  for (let r = -2; r <= 1; r++) at(2, r, pole);
  for (let c = 3; c <= 5; c++) for (const r of [-2, -1]) at(c, r, '#D77757');
  return px;
}

// ---------- the thinking bubble, above the crab's top row ----------
export function thinkPixels(now, top) {
  const step = Math.floor(now / 280) % 3, px = [];
  for (let r = top - 6; r <= top - 2; r++) for (let c = -5; c <= 5; c++) {
    if (!((r === top - 6 || r === top - 2) && (c === -5 || c === 5))) px.push([c, r, '#FFFFFF']);
  }
  px.push([-6, top - 1, '#FFFFFF']);
  [-3, 0, 3].forEach((c, i) => px.push([c, top - 4, i === step ? '#4A403A' : '#BDB2A7']));
  return px;
}

// ---------- items held in a claw (drawn facing right, (0, 0) at the hand) ----------
const CROSSBOW = { o: '#2A1A09', P: '#4D2E11', W: '#90581F', w: '#6D4218', k: '#444444', g: '#8A8A8A', a: '#896727', h: '#D8D8D8' };
const BOW = [[-1, -3, '#493615'], [0, -2, '#896727'], [0, -1, '#684E1E'], [0, 0, '#6B6B6B'], [0, 1, '#684E1E'], [0, 2, '#896727'], [-1, 3, '#493615']];
export const ITEMS = {
  rocket: [[0, 0, '#B8312F'], [1, 0, '#F1ECE4'], [2, 0, '#B8312F'], [3, 0, '#8C8C8C'], [-1, 0, '#5A3E2B']],
  pearl: [[0, -1, '#7FE0CF'], [1, -1, '#1D5E57'], [0, 0, '#2E8C7F'], [1, 0, '#1D5E57']],
  flint: [[0, 0, '#8C8C8C'], [1, 0, '#8C8C8C'], [1, -1, '#B0B0B0'], [2, -1, '#3C3C3C'], [2, -2, '#3C3C3C']],
  obsidian: [[0, -1, '#2E2142'], [1, -1, '#150F20'], [0, 0, '#150F20'], [1, 0, '#4B347A']],
  slime: [[0, -1, '#8FD87E'], [1, -1, '#4E9A43'], [0, 0, '#4E9A43'], [1, 0, '#8FD87E']],
  drink: DRINK,
  file: [[0, -1, '#FFFFFF'], [1, -1, '#C9D3E0'], [0, 0, '#9AA7B8'], [1, 0, '#FFFFFF']],
  swordUp: [[0, 0, '#6B4A2B'], [-1, -1, '#3A3A3A'], [0, -1, '#3A3A3A'], [1, -1, '#3A3A3A'],
    [0, -2, '#D7DCE3'], [0, -3, '#D7DCE3'], [0, -4, '#D7DCE3'], [0, -5, '#D7DCE3'], [0, -6, '#F4F6F8']],
  swordDiag: [[0, 0, '#6B4A2B'], [-1, -1, '#3A3A3A'], [1, 1, '#3A3A3A'],
    [1, -1, '#D7DCE3'], [2, -2, '#D7DCE3'], [3, -3, '#D7DCE3'], [4, -4, '#D7DCE3'], [5, -5, '#F4F6F8']],
  swordForward: [[0, 0, '#6B4A2B'], [1, -1, '#3A3A3A'], [1, 0, '#3A3A3A'], [1, 1, '#3A3A3A'],
    [2, 0, '#D7DCE3'], [3, 0, '#D7DCE3'], [4, 0, '#D7DCE3'], [5, 0, '#D7DCE3'], [6, 0, '#F4F6F8']],
  // A bow held upright with the string behind it, and drawn back with an arrow on it (the Skeleton's).
  bow: [...BOW, [-1, -2, '#444444'], [-1, -1, '#444444'], [-1, 1, '#444444'], [-1, 2, '#444444']],
  bowDrawn: [...BOW, [-2, -2, '#444444'], [-2, -1, '#444444'], [-2, 1, '#444444'], [-2, 2, '#444444'],
    [-4, 0, '#C6C6C6'], [-3, 0, '#E0E0E0'], [-2, 0, '#896727'], [-1, 0, '#896727'], [1, 0, '#D8D8D8']],
  // Gripped in a block hand (pose.grip), pointing up from the handle: they turn with the arm.
  diamondSword: [[0, 1, '#0E3F36'], [0, 0, '#684E1E'], [0, -1, '#896727'], [-1, -2, '#0E3F36'], [0, -2, '#1E8A77'], [1, -2, '#0E3F36'],
    [0, -3, '#2BC7AC'], [0, -4, '#2BC7AC'], [0, -5, '#33EBCB'], [0, -6, '#33EBCB'], [0, -7, '#A4FDF0']],
  goldSword: [[0, 1, '#825D16'], [0, 0, '#684E1E'], [0, -1, '#896727'], [-1, -2, '#825D16'], [0, -2, '#DC9613'], [1, -2, '#825D16'],
    [0, -3, '#E9B115'], [0, -4, '#EAEE57'], [0, -5, '#FDFF76'], [0, -6, '#FDFF76'], [0, -7, '#FFFFFF']],
  diamondPickaxe: sprite(`
.oLLLo.
oLmhmLo
o..H..o
...h...
...H...
...h...
...H...
...h...
`, { o: '#0E3F36', L: '#33EBCB', m: '#27B29A', h: '#684E1E', H: '#896727' }, -3, -6),
  poppy: sprite(`
RRr
RdR
rRr
.g.
Lg.
.g.
.g.
`, { R: '#ED302C', r: '#BF2529', d: '#742303', g: '#4A8F28', L: '#265A25' }, -1, -6),
  grassBlock: sprite(`
acb
Dml
mlD
`, { a: '#74B44A', b: '#6CAC42', c: '#8DBC5D', D: '#593D29', m: '#79553A', l: '#966C4A' }, -1, -1),
  goldIngot: sprite(`
.YWY
oIId
`, { Y: '#FDF55F', W: '#FFFDE0', o: '#B26411', I: '#E9B115', d: '#752802' }, -2, -3),   // held up on the hand
  // A crossbow, the bow at the front: unloaded, its string being drawn, and loaded with a bolt.
  crossbow: sprite(`
.oPPPo.
oggWggo
...w...
...W...
...w...
...k...
...W...
...w...
`, CROSSBOW, -3, -6),
  crossbowPull: sprite(`
.oPPPo.
o..W..o
.g.w.g.
..gWg..
...w...
...k...
...W...
...w...
`, CROSSBOW, -3, -6),
  crossbowCharged: sprite(`
...h...
.oPaPo.
o..a..o
.g.a.g.
..gag..
...g...
...k...
...W...
...w...
`, CROSSBOW, -3, -7),
  // Sweet berries held in a mouth (a fox's): the stem in the mouth, the berries hanging below.
  sweetBerries: sprite(`
gL
Rr
rd
`, { g: '#4C7A34', L: '#6E9B45', R: '#E65A5A', r: '#C33A3E', d: '#8A1E2C' }, -1, 0),
  // The glowing disc that opens a wormhole, before it's thrown.
  disc: [[1, -1, '#5FD8F5'], [0, 0, '#5FD8F5'], [1, 0, '#FFFFFF'], [2, 0, '#5FD8F5'], [1, 1, '#2E86E8']],
};
export function itemAt(name, hand, facing) {
  const art = ITEMS[name];
  if (!art) return [];
  return art.map(([c, r, t]) => [hand[0] + (facing < 0 ? -c : c), hand[1] + r, t]);
}

// ---------- blocks placed in the world (b x b pixels, (0, 0) at the block's top-left) ----------
export function slimePixels(b) {
  const px = [];
  for (let r = 0; r < b; r++) for (let c = 0; c < b; c++) {
    const edge = r === 0 || c === 0 || r === b - 1 || c === b - 1;
    const core = r > 1 && c > 1 && r < b - 2 && c < b - 2;
    px.push([c, r, edge ? '#4E9A43' : core ? '#B4EBA6' : '#8FD87E']);
  }
  px.push([1, 1, '#DDF7D4']);
  return px;
}

export function obsidianPixels(b, seed) {
  const px = [];
  for (let r = 0; r < b; r++) for (let c = 0; c < b; c++) {
    const k = hash(c + seed * 7, r + seed * 3, seed);
    px.push([c, r, k < 0.12 ? '#4B347A' : k < 0.34 ? '#2E2142' : k < 0.4 ? '#0B0811' : '#150F20']);
  }
  return px;
}

// The swirling inside of a lit portal, w x h pixels.
export function portalFill(w, h, now, seed) {
  const px = [], t = Math.floor(now / 90);
  for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) {
    const wave = Math.sin((r + now / 160) * 0.8 + c * 0.9 + seed);
    const k = hash(c + seed, r + Math.floor(wave * 2), t);
    px.push([c, r, k < 0.18 ? '#C08BFF' : k < 0.5 ? '#9B4DFF' : k < 0.82 ? '#7A2BD9' : '#5A1AA8']);
  }
  return px;
}

// ---------- the grappling hook (the desktop companions' ender pearl) ----------
// A little black gun pointing right, its grip in the hand at (0, 0). The rope comes out of the
// muzzle, where the tip of the loaded hook shows. The hook points right with its tip at (0, 0),
// and the rope is tied on at its ring, two shades of hemp along its length.
export const HOOK_GUN = {
  art: sprite(`
.hhhh.
KKKKKM
.Kg...
.K....
`, { h: '#4A4A56', K: '#1E1E24', M: '#6B6B78', g: '#E9B115' }, -1, -2),
  muzzle: [5, -1], loaded: '#D7DCE3',
};
export const HOOK = {
  art: sprite(`
...h.
....h
osssH
....h
...h.
`, { h: '#A7B0BA', s: '#6B737C', H: '#E3E8EE', o: '#4A5058' }, -4, -2),
  ring: [-4, 0],
};
export const ROPE = ['#C9A66B', '#8A6A44'];

// ---------- the wormhole (the desktop companions' nether portal) ----------
// A swirling oval w x h pixels, standing on the surface with its middle at x = 0: two spiral
// arms turning around a bright core, a twinkling rim, a soft glow around it and specks drifting
// in. open (0..1, a bit more while it springs open) grows it up out of a flat disc.
export const WORMHOLE_COLORS = ['#8BE9FA', '#FFFFFF', '#3C95EE', '#B6F7FF'];   // its sparkles
const WORM = { core: '#F2FDFF', inner: ['#9BEAFB', '#3460DA'], outer: ['#4FB3F2', '#232C96'], rim: '#8BE9FA', glow: '#8BE9FA4D' };   // [arm, between arms]
export function wormholePixels(w, h, open, now, seed) {
  const k = Math.min(1, open), W = Math.max(1, Math.round((3 + (w - 3) * k) * Math.max(1, open))), H = Math.max(1, Math.round(h * open));
  const left = -Math.floor(W / 2), top = -H, px = [];
  const turn = now / 1400 + seed, twinkle = Math.floor(now / 110);
  for (let r = -1; r < H; r++) for (let c = -1; c <= W; c++) {      // (no glow under the surface)
    const nx = ((c + 0.5) / W) * 2 - 1, ny = ((r + 0.5) / H) * 2 - 1, d = Math.hypot(nx, ny);
    let color;
    if (d > 1) {                                       // the glow, a pixel around it
      if (Math.hypot((nx * W) / (W + 2), (ny * H) / (H + 2)) > 1) continue;
      color = WORM.glow;
    } else {
      const a = Math.atan2(ny * H, nx * W) / (2 * Math.PI);
      const arm = (((2 * a + 2.4 * (1 - d) - turn) % 1) + 1) % 1 < 0.5 ? 0 : 1;   // two arms, curling in
      color = d > 0.8 ? (hash(c, r, twinkle + seed * 31) < 0.14 ? '#FFFFFF' : WORM.rim)
        : d > 0.48 ? WORM.outer[arm] : d > 0.2 ? WORM.inner[arm] : WORM.core;
    }
    px.push([left + c, top + r, color]);
  }
  if (open >= 0.95) for (let i = 0; i < 6; i++) {      // specks drifting in, lost in the swirl at its rim
    const f = (now / 1100 + i / 6 + seed) % 1, ang = i * 2.4 + f * 2.4, rf = 1.5 - 1.3 * f;
    if (rf < 0.9) continue;
    px.push([Math.round(left + W / 2 - 0.5 + (Math.cos(ang) * rf * W) / 2), Math.round(top + H / 2 - 0.5 + (Math.sin(ang) * rf * H) / 2), i % 2 ? '#FFFFFF' : '#B6F7FF']);
  }
  return px;
}
// The disc in flight, spinning: face on, then edge on.
export const discPixels = now => (Math.floor(now / 70) % 2 ? [[0, -1, '#5FD8F5'], [0, 0, '#FFFFFF'], [0, 1, '#2E86E8']]
  : ITEMS.disc.map(([c, r, t]) => [c - 1, r, t]));

// ---------- little sprites for particles ----------
export const HEART = sprite(`
.r.r.
rRrrr
.rrr.
..r..
`, { r: '#FF4F6D', R: '#FFB3C1' });
export const ZED = sprite(`
zzz
.z.
zzz
`, { z: '#EAE4FF' });
export const BANG = [[0, 0, '#FFE38A'], [0, 1, '#FFE38A'], [0, 2, '#FFE38A'], [0, 4, '#FFE38A']];
